<template>
  <section class="py-12 sm:py-16 md:py-20 lg:py-24 xl:py-32 bg-[#f4f4f4] relative overflow-hidden">
    <div
      class="absolute inset-0 bg-gradient-to-b from-transparent via-[#fe6d0d]/5 to-transparent"
    ></div>

    <div class="container mx-auto px-4 sm:px-6 lg:px-8 xl:px-12 relative z-10">
      <div class="text-center mb-12 sm:mb-16">
        <h2
          class="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-black text-[#0b105d] mb-4"
          data-aos="fade-up"
        >
          {{ $t('aboutUsPage.companyTimeline.title') }}
        </h2>
        <div
          class="w-16 sm:w-20 md:w-24 h-1 bg-gradient-to-r from-[#fe6d0d] to-[#ffb47c] mx-auto mb-4"
          data-aos="fade-up"
          data-aos-delay="100"
        ></div>
        <p
          class="text-base sm:text-lg text-gray-700 max-w-2xl mx-auto"
          data-aos="fade-up"
          data-aos-delay="200"
        >
          {{ $t('aboutUsPage.companyTimeline.subtitle') }}
        </p>
      </div>

      <div class="relative max-w-6xl mx-auto">
        <!-- Timeline Line -->
        <div
          class="hidden lg:block absolute left-1/2 transform -translate-x-1/2 w-1 h-full bg-gradient-to-b from-[#fe6d0d] via-[#ffb47c] to-[#fe6d0d] rounded-full"
          data-aos="fade-up"
          data-aos-delay="300"
        ></div>

        <!-- Mobile Timeline Line -->
        <div
          class="lg:hidden absolute left-6 sm:left-8 top-0 w-0.5 h-full bg-gradient-to-b from-[#fe6d0d] via-[#ffb47c] to-[#fe6d0d] rounded-full"
          data-aos="fade-up"
          data-aos-delay="300"
        ></div>

        <div class="space-y-8 sm:space-y-12 lg:space-y-16">
          <div
            v-for="(item, index) in timelineItems"
            :key="index"
            class="relative"
            data-aos="fade-up"
            :data-aos-delay="400 + index * 150"
          >
            <!-- Desktop Layout -->
            <div
              class="hidden lg:flex items-center"
              :class="{ 'flex-row-reverse': index % 2 === 1 }"
            >
              <!-- Content Card -->
              <div class="w-5/12 relative group">
                <div
                  class="absolute -inset-1 bg-gradient-to-r from-[#fe6d0d] to-[#ffb47c] rounded-2xl blur opacity-20 group-hover:opacity-40 transition-opacity duration-500"
                ></div>
                <div
                  class="relative bg-white rounded-2xl p-6 sm:p-8 shadow-xl group-hover:shadow-2xl transition-all duration-300 border border-gray-100"
                >
                  <div class="flex items-start justify-between mb-4">
                    <div
                      class="flex items-center space-x-3 px-4 py-2 rounded-full bg-gradient-to-r from-[#fe6d0d]/10 to-[#ffb47c]/10 border border-[#ffb47c]/20"
                    >
                      <CalendarIcon class="w-4 h-4 text-[#fe6d0d]" />
                      <span class="text-sm font-semibold text-[#0b105d]">{{ item.year }}</span>
                    </div>
                    <div class="w-2 h-2 bg-[#ffb47c] rounded-full animate-pulse"></div>
                  </div>
                  <h3 class="text-xl sm:text-2xl font-bold text-[#0b105d] mb-3">
                    {{ $t(item.titleKey) }}
                  </h3>
                  <p class="text-gray-700 leading-relaxed text-sm sm:text-base">
                    {{ $t(item.descriptionKey) }}
                  </p>
                  <div v-if="item.achievements" class="mt-4 flex flex-wrap gap-2">
                    <span
                      v-for="achievement in item.achievements"
                      :key="achievement"
                      class="px-3 py-1 text-xs font-medium bg-gradient-to-r from-[#fe6d0d]/10 to-[#ffb47c]/10 text-[#0b105d] rounded-full border border-[#ffb47c]/20"
                    >
                      {{ $t(achievement) }}
                    </span>
                  </div>
                </div>
              </div>

              <!-- Timeline Node -->
              <div class="w-2/12 flex justify-center relative">
                <div class="relative z-10">
                  <div
                    class="w-16 h-16 bg-gradient-to-br from-[#fe6d0d] to-[#ffb47c] rounded-full border-4 border-white shadow-lg flex items-center justify-center group-hover:scale-110 transition-transform duration-300"
                  >
                    <component :is="item.icon" class="w-8 h-8 text-white" />
                  </div>
                  <!-- Connecting dots -->
                  <div
                    class="absolute top-1/2 w-8 h-0.5 bg-gradient-to-r from-[#fe6d0d] to-[#ffb47c]"
                    :class="index % 2 === 0 ? 'right-full' : 'left-full'"
                  ></div>
                </div>
              </div>

              <!-- Spacer -->
              <div class="w-5/12"></div>
            </div>

            <!-- Mobile Layout -->
            <div class="lg:hidden flex items-start space-x-4 sm:space-x-6">
              <!-- Timeline Node -->
              <div class="flex-shrink-0 relative">
                <div
                  class="w-12 h-12 sm:w-14 sm:h-14 bg-gradient-to-br from-[#fe6d0d] to-[#ffb47c] rounded-full border-3 border-white shadow-lg flex items-center justify-center"
                >
                  <component :is="item.icon" class="w-6 h-6 sm:w-7 sm:h-7 text-white" />
                </div>
                <div
                  class="absolute top-1/2 left-full w-4 h-0.5 bg-gradient-to-r from-[#fe6d0d] to-[#ffb47c]"
                ></div>
              </div>

              <!-- Content Card -->
              <div class="flex-grow relative group">
                <div
                  class="absolute -inset-1 bg-gradient-to-r from-[#fe6d0d] to-[#ffb47c] rounded-xl blur opacity-20 group-hover:opacity-40 transition-opacity duration-500"
                ></div>
                <div
                  class="relative bg-white rounded-xl p-4 sm:p-6 shadow-lg group-hover:shadow-xl transition-all duration-300 border border-gray-100"
                >
                  <div class="flex items-center justify-between mb-3">
                    <div
                      class="flex items-center space-x-2 px-3 py-1.5 rounded-full bg-gradient-to-r from-[#fe6d0d]/10 to-[#ffb47c]/10 border border-[#ffb47c]/20"
                    >
                      <CalendarIcon class="w-3 h-3 text-[#fe6d0d]" />
                      <span class="text-xs font-semibold text-[#0b105d]">{{ item.year }}</span>
                    </div>
                    <div class="w-1.5 h-1.5 bg-[#ffb47c] rounded-full animate-pulse"></div>
                  </div>
                  <h3 class="text-lg sm:text-xl font-bold text-[#0b105d] mb-2">
                    {{ $t(item.titleKey) }}
                  </h3>
                  <p class="text-gray-700 leading-relaxed text-sm">
                    {{ $t(item.descriptionKey) }}
                  </p>
                  <div v-if="item.achievements" class="mt-3 flex flex-wrap gap-1.5">
                    <span
                      v-for="achievement in item.achievements"
                      :key="achievement"
                      class="px-2 py-0.5 text-xs font-medium bg-gradient-to-r from-[#fe6d0d]/10 to-[#ffb47c]/10 text-[#0b105d] rounded-full border border-[#ffb47c]/20"
                    >
                      {{ $t(achievement) }}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Future Vision Card -->
        <div class="mt-12 sm:mt-16 lg:mt-20" data-aos="zoom-in" data-aos-delay="800">
          <div class="relative group max-w-4xl mx-auto">
            <div
              class="absolute -inset-2 bg-gradient-to-r from-[#fe6d0d] via-[#ffb47c] to-[#fe6d0d] rounded-3xl blur-xl opacity-30 group-hover:opacity-50 transition-opacity duration-500 animate-pulse"
            ></div>
            <div
              class="relative bg-gradient-to-br from-[#0b105d] to-[#0c1260] rounded-3xl p-8 sm:p-10 md:p-12 text-center shadow-2xl border border-[#f4f4f4]/10"
            >
              <div
                class="absolute inset-0 bg-gradient-to-br from-[#fe6d0d]/5 to-[#ffb47c]/5 rounded-3xl"
              ></div>
              <div class="relative z-10">
                <div
                  class="w-20 h-20 sm:w-24 sm:h-24 bg-gradient-to-br from-[#fe6d0d] to-[#ffb47c] rounded-full flex items-center justify-center mx-auto mb-6 shadow-xl"
                >
                  <RocketIcon class="w-10 h-10 sm:w-12 sm:h-12 text-white" />
                </div>
                <h3
                  class="text-2xl sm:text-3xl md:text-4xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-[#fe6d0d] to-[#ffb47c] mb-4"
                >
                  {{ $t('aboutUsPage.companyTimeline.future.title') }}
                </h3>
                <p class="text-[#f4f4f4]/90 text-base sm:text-lg leading-relaxed max-w-2xl mx-auto">
                  {{ $t('aboutUsPage.companyTimeline.future.description') }}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script setup lang="ts">
import {
  AppleIcon,
  BrainCircuitIcon,
  BrainIcon,
  CalendarIcon,
  ChartNoAxesCombinedIcon,
  EarthIcon,
  FlaskConicalIcon,
  LandmarkIcon,
  LightbulbIcon,
  RocketIcon,
  ShoppingCartIcon,
  TrendingUpIcon,
} from 'lucide-vue-next'

const timelineItems = [
  {
    year: '2020',
    titleKey: 'aboutUsPage.companyTimeline.items.2020.title',
    descriptionKey: 'aboutUsPage.companyTimeline.items.2020.description',
    icon: LightbulbIcon,
    achievements: [
      'aboutUsPage.companyTimeline.items.2020.achievements.idea',
      'aboutUsPage.companyTimeline.items.2020.achievements.research',
    ],
  },
  {
    year: '2021',
    titleKey: 'aboutUsPage.companyTimeline.items.2021.title',
    descriptionKey: 'aboutUsPage.companyTimeline.items.2021.description',
    icon: FlaskConicalIcon,
    achievements: [
      'aboutUsPage.companyTimeline.items.2021.achievements.status',
      'aboutUsPage.companyTimeline.items.2021.achievements.team',
    ],
  },
  {
    year: '2022',
    titleKey: 'aboutUsPage.companyTimeline.items.2022.title',
    descriptionKey: 'aboutUsPage.companyTimeline.items.2022.description',
    icon: AppleIcon,
    achievements: [
      'aboutUsPage.companyTimeline.items.2022.achievements.status',
      'aboutUsPage.companyTimeline.items.2022.achievements.brand',
    ],
  },
  {
    year: '2023-1',
    titleKey: 'aboutUsPage.companyTimeline.items.2023-1.title',
    descriptionKey: 'aboutUsPage.companyTimeline.items.2023-1.description',
    icon: BrainIcon,
    achievements: [
      'aboutUsPage.companyTimeline.items.2023-1.achievements.prototype',
      'aboutUsPage.companyTimeline.items.2023-1.achievements.tech',
    ],
  },
  {
    year: '2023-2',
    titleKey: 'aboutUsPage.companyTimeline.items.2023-2.title',
    descriptionKey: 'aboutUsPage.companyTimeline.items.2023-2.description',
    icon: TrendingUpIcon,
    achievements: [
      'aboutUsPage.companyTimeline.items.2023-2.achievements.investment',
      'aboutUsPage.companyTimeline.items.2023-2.achievements.tech',
    ],
  },
  {
    year: '2024-1',
    titleKey: 'aboutUsPage.companyTimeline.items.2024-1.title',
    descriptionKey: 'aboutUsPage.companyTimeline.items.2024-1.description',
    icon: BrainCircuitIcon,
    achievements: [
      'aboutUsPage.companyTimeline.items.2024-1.achievements.ai',
      'aboutUsPage.companyTimeline.items.2024-1.achievements.testing',
    ],
  },
  {
    year: '2024-2',
    titleKey: 'aboutUsPage.companyTimeline.items.2024-2.title',
    descriptionKey: 'aboutUsPage.companyTimeline.items.2024-2.description',
    icon: ChartNoAxesCombinedIcon,
    achievements: [
      'aboutUsPage.companyTimeline.items.2024-2.achievements.growth',
      'aboutUsPage.companyTimeline.items.2024-2.achievements.customers',
    ],
  },
  {
    year: '2024-3',
    titleKey: 'aboutUsPage.companyTimeline.items.2024-3.title',
    descriptionKey: 'aboutUsPage.companyTimeline.items.2024-3.description',
    icon: LandmarkIcon,
    achievements: [
      'aboutUsPage.companyTimeline.items.2024-3.achievements.investment',
      'aboutUsPage.companyTimeline.items.2024-3.achievements.future',
    ],
  },
  {
    year: '2025-1',
    titleKey: 'aboutUsPage.companyTimeline.items.2025-1.title',
    descriptionKey: 'aboutUsPage.companyTimeline.items.2025-1.description',
    icon: EarthIcon,
    achievements: [
      'aboutUsPage.companyTimeline.items.2025-1.achievements.global',
      'aboutUsPage.companyTimeline.items.2025-1.achievements.expansion',
    ],
  },
  {
    year: '2025-3',
    titleKey: 'aboutUsPage.companyTimeline.items.2025-3.title',
    descriptionKey: 'aboutUsPage.companyTimeline.items.2025-3.description',
    icon: ShoppingCartIcon,
    // achievements: [
    //   'aboutUsPage.companyTimeline.items.2025-3.achievements.launch',
    //   'aboutUsPage.companyTimeline.items.2025-3.achievements.product',
    // ],
  },
  {
    year: '2025-4',
    titleKey: 'aboutUsPage.companyTimeline.items.2025-4.title',
    descriptionKey: 'aboutUsPage.companyTimeline.items.2025-4.description',
    icon: ShoppingCartIcon,
    achievements: [
      'aboutUsPage.companyTimeline.items.2025-4.achievements.launch',
      'aboutUsPage.companyTimeline.items.2025-4.achievements.product',
    ],
  },
]
</script>

<style scoped>
.border-3 {
  border-width: 3px;
}
</style>
